import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

const fadeInSlide = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedLabel = styled.label`
  opacity: 0;
  animation: ${fadeInSlide} 0.5s ease-out forwards;
  animation-delay: 0.2s;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
`;

const AnimatedChoiceContainer = styled.div`
  margin-top: 0.5rem;
  opacity: 0;
  animation: ${fadeInSlide} 0.5s ease-out forwards;
  animation-delay: 0.4s;

  /* Flex container for the choices */
  display: flex;
  justify-content: space-between;
  align-items: flex-start; /* Align items at the top */
  width: 100%;
`;

const Description = styled.div`
  font-weight: 300;
  font-size: 12px;
  margin-top: 10px;
  font-family: 'Raleway', sans-serif;
`;

const Qualifier = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
`;

/* --- Updated ChoiceButton styling --- */
const ChoiceButton = styled.button`
  flex: 1;
  margin: 0 5px;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;

  /* Create a distinct box around each choice */
  background-color: ${(props) => (props.isSelected ? "#D5E2F9" : "#fff")};
  border: 2px solid
    ${(props) => (props.isSelected ? "#2D70E2" : "#ddd")};
  border-radius: 8px;

  /* Use flex for internal layout */
  display: flex;
  flex-direction: column;
  align-items: center;

  /* Fixed height for consistency */
  min-height: 50px;
  padding: 6px;

  &:hover {
    background-color: #d5e2f9;
    border-color: #2d70e2;
  }

  @media (max-width: 500px) {
    min-width: 18%;
    padding: 8px 4px;
  }
`;

const EmojiContainer = styled.div`
  /* This ensures the emoji is always at the same vertical position */
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmojiSpan = styled.span`
  font-size: 24px;
`;

const LabelContainer = styled.div`
  /* Fixed height for the label space to maintain consistency */
  min-height: 30px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-top: 0px;
`;

const LabelSpan = styled.span`
  font-size: 12px;
  text-align: center;

  @media (max-width: 500px) {
    font-size: 9px;
  }
`;

export default function QuestionLikert({ question, currentAnswer, onAnswerChange }) {
  // If the question properties don't include a scale, default to 5
  const scaleNum = question?.properties?.scale || 5;
  
  // Get the scale labels from the question properties, or use defaults
  const scaleLabels = question?.properties?.scaleLabels || [
    "Strongly Disagree", 
    "Disagree",
    "Neutral",
    "Agree",
    "Strongly Agree"
  ];

  // Check if emojis should be used
  const useEmojis = question?.properties?.useEmojis || false;
  
  // Get emoji array if available, otherwise use empty array
  const scaleEmojis = question?.properties?.scaleEmojis || Array(scaleNum).fill(null);

  // Dynamically build the choice array
  const choices = Array.from({ length: scaleNum }, (_, i) => {
    return {
      value: i+1,
      // Use the label from scaleLabels array if available
      label: scaleLabels[i] || "",
      // If useEmojis is true, use the emoji from scaleEmojis array, otherwise use the value as string
      emoji: useEmojis ? scaleEmojis[i] : String(i + 1),
    };
  });

  // Track the selected value
  const [selectedValue, setSelectedValue] = useState(null);

  // On mount or question change, pre-fill if there's an existing answer
  useEffect(() => {
    if (currentAnswer?.response) {
      setSelectedValue(currentAnswer.response);
    } else {
      setSelectedValue(null);
    }
  }, [currentAnswer]);

  const handleChoiceClick = (value) => {
    setSelectedValue(value);
    // Tell the parent about the new value
    onAnswerChange(question.uniqueId, {"response":value});
  };

  return (
    <div style={{ marginTop: "1rem" }}>
      {question.qualifier && <Qualifier>{question.qualifier}</Qualifier>}
      <AnimatedLabel>{question.question}</AnimatedLabel>
      {question.description && <Description>{question.description}</Description>}

      <AnimatedChoiceContainer>
        {choices.map((choice) => {
          const isSelected = choice.value === selectedValue;
          return (
            <ChoiceButton
              key={choice.value}
              isSelected={isSelected}
              onClick={() => handleChoiceClick(choice.value)}
            >
              <EmojiContainer>
                {/* Only render emoji if it's not null */}
                {choice.emoji && <EmojiSpan>{choice.emoji}</EmojiSpan>}
              </EmojiContainer>
              <LabelContainer>
                {choice.label && <LabelSpan>{choice.label}</LabelSpan>}
              </LabelContainer>
            </ChoiceButton>
          );
        })}
      </AnimatedChoiceContainer>
    </div>
  );
}
